import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { TId } from "@components/table";
import { TToolbarItem } from "@components/toolbar/Toolbar.types";
import { TEntityKey } from "@evala/odata-metadata";
import { IPaymentDocumentItemEntity, IProformaInvoiceIssuedEntity } from "@odata/GeneratedEntityTypes";
import { PaymentDocumentItemTypeCode } from "@odata/GeneratedEnums";
import { isAccountAssignmentCompany, isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { isDefined, roundToDecimalPlaces } from "@utils/general";
import React, { ReactElement } from "react";

import BindingContext, { IEntity } from "../../odata/BindingContext";
import TestIds from "../../testIds";
import { formatCurrency } from "../../types/Currency";
import { FormStorage } from "../../views/formView/FormStorage";
import { PAIR_ACTION_ID } from "../asset/PairingWithAssetTableView";
import { loadExchangeRate } from "../documents/Document.utils";
import { PairingTitle, PairingValue } from "./bankTransactions/BankTransactions.styles";
import {
    getBankItemDefaultDateAccountingTransaction,
    getCorrectExchangeRate,
    getCorrectTranAmount, getPairDataForRow,
    getTransactionTypeFromEntity,
    hasAdditionalExRate,
    IPairTableViewBaseProps,
    savePairChanges
} from "./bankTransactions/BankTransactions.utils";
import { AmountDialog } from "./PairTableAmountDialog";
import { PairTableViewBaseForExtend } from "./PairTableViewBase";


interface IPaymentOrigData {
    Amount: number;
    ClearedAmount: number;
}

export interface IPaymentDocumentItemEntityExpanded extends IPaymentDocumentItemEntity {
    ExchangeGain?: number;
    IsTaxDocument?: boolean;
    Gains?: IPaymentDocumentItemEntity[];
    OrigData?: IPaymentOrigData;
}

// prefix Doc --> values of paired DOCUMENT
// prefix Tran --> values of bank transaction (stored in rootStorage)
class PairTableView<P extends IPairTableViewBaseProps = IPairTableViewBaseProps> extends PairTableViewBaseForExtend<P> {
    needConfirmDialog: boolean;

    get hasAccountAssignment(): boolean {
        return isAccountAssignmentCompany(this.props.storage.context);
    }

    getPairedDocuments(): Record<TEntityKey, IPaymentDocumentItemEntityExpanded> {
        return this.props.storage.getCustomData().pairedDocuments || {};
    }

    savePairChanges = async (storage: FormStorage, pairedDocs: Record<TEntityKey, IPaymentDocumentItemEntityExpanded>): Promise<void> => {
        const type = this.getTransactionType();
        this.props.setBusy?.(true);
        await savePairChanges(this.props.rootStorage, pairedDocs, type);
        this.props.rootStorage.setDirty(this.props.rootStorage.data.bindingContext); // to allow draft save (shouldDisableDraftButton function)
        this.props.setBusy?.(false);
    };

    processRowPairing = async (rowId: TId): Promise<Record<TEntityKey, IPaymentDocumentItemEntityExpanded>> => {
        const { storage, rootStorage } = this.props;
        const bc = rowId as BindingContext;
        const key = bc.getKey();

        let pairedDoc = this.getPairedDocuments();

        if (pairedDoc[key]) {
            delete pairedDoc[key];
        } else {
            if (this.props.skipAmountPair) {
                if (this.props.isSingleSelect) {
                    pairedDoc = { [key]: {} };
                } else {
                    pairedDoc[key] = {};
                }
            } else {
                this.props.setBusy?.(true);

                const type = rootStorage.oData.metadata.getTypeForPath(this.getRowEntitySet(bc));
                const row = getRow(storage.tableAPI.getState().rows, bc);
                const document = row?.customData.entity;
                const numberOurs = document.NumberOurs;
                if (isCashBasisAccountingCompany(this.context)) {
                    this.fetchDocumentCategories(document);
                }
                const transDate = rootStorage.data.entity.DateBankTransaction ?? rootStorage.data.entity.DateIssued;

                const docCurrency = document.TransactionCurrency?.Code;
                const docAmount = getCorrectTranAmount(rootStorage, document.Id, Math.abs(document.TransactionAmountDue), docCurrency,);
                const docExchangeRate = await getCorrectExchangeRate({
                    id: document?.Id,
                    date: transDate,
                    currency: docCurrency
                }, this.context);

                if (!docExchangeRate) {
                    this.props.setBusy?.(false);
                    // if exchange rate is not found, we show dialog to user with information,
                    // there is no exchange rate for the date. Row cannot be paired.
                    await this.props.confirmationDialog.open({
                        content: storage.t("Common:Errors.NoExchangeRateForGivenDate"),
                        allowCancel: false
                    });
                    return null;
                }

                const amounts = this.getPairedAmounts();
                const _transAmount = Math.abs(this.props.rootStorage.data.entity.TransactionAmount || 0);
                const tranCurrency = this.getTransactionCurrency();
                const tranExRate = this.getTransactionExchangeRate();
                const docType = document.DocumentTypeCode;

                const _hasAdditionalExRate = hasAdditionalExRate(docCurrency, tranCurrency, this.context);

                let defaultExchangeRate;
                if (_hasAdditionalExRate) {
                    // this is case when we pair transaction and document with different currencies but non of them is system
                    defaultExchangeRate = await loadExchangeRate(this.props.storage, docCurrency, transDate);
                    if (!defaultExchangeRate) {
                        this.needConfirmDialog = true;
                        this.props.storage.setCustomData({
                            amountDialogRow: row
                        });
                    }
                }

                const data = getPairDataForRow({
                    amounts,
                    tranExRate,
                    docAmount,
                    docType,
                    defaultExchangeRate,
                    docCurrency,
                    tranCurrency,
                    originalTransactionAmount: _transAmount,
                    tranType: getTransactionTypeFromEntity(this.props.rootStorage),
                    docExchangeRate
                }, this.context);

                pairedDoc[key] = {
                    Amount: data.amount,
                    TransactionAmount: data.transactionAmount,
                    ExchangeGain: data.exchangeGain,
                    ExchangeRate: defaultExchangeRate,
                    ClearedAmount: data.ratedAmount,
                    IsTaxDocument: row.customData.entity.IsTaxDocument,
                    LinkedDocument: {
                        TransactionAmount: document?.TransactionAmount,
                        ExchangeRatePerUnit: docExchangeRate,
                        TransactionCurrencyCode: docCurrency,
                        BusinessPartner: document?.BusinessPartner,
                        TransactionCurrency: {
                            Code: docCurrency
                        },
                        TransactionAmountDue: row.customData.entity.TransactionAmountDue,
                        Id: parseInt(key as string),
                        NumberOurs: numberOurs as string,
                        DocumentTypeCode: row.customData.entity.DocumentType.Code,
                        _metadata: {
                            "": { type: `#${type.getFullName()}` }
                        }
                    }
                };

                if (this.hasAccountAssignment) {
                    const dateAccountingTrans = getBankItemDefaultDateAccountingTransaction(this.props.rootStorage);

                    pairedDoc[key] = {
                        ...pairedDoc[key],
                        DateAccountingTransaction: dateAccountingTrans,
                        LinkedDocument: {
                            ...pairedDoc[key].LinkedDocument,
                            DateAccountingTransaction: document?.DateAccountingTransaction
                        }
                    };
                }
            }
        }

        return pairedDoc;
    }

    setOriginItems(): void {
        const items = this.props.rootStorage.data.entity.Items as IPaymentDocumentItemEntity[];
        const pairedDocuments: Record<TEntityKey, IPaymentDocumentItemEntityExpanded> = {};

        // we convert Items to hashtable, this hashtable object merge exchange gain and payment to one single object (for more simple operations)
        // at the end when saving, we split this object to two posting items (of course only in case there is any exchange gain (pairing with different currencies then CZK))
        for (const item of (items || [])) {
            if (item.LinkedDocument?.Id) {
                const key = item.LinkedDocument.Id;
                if (item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment) {
                    const gains = items.filter(_item => (_item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain || _item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeLoss) && _item.LinkedDocument.Id === item.LinkedDocument.Id);
                    pairedDocuments[key] = {
                        ...pairedDocuments[key],
                        Id: item.Id,
                        IsTaxDocument: (item.LinkedDocument as IProformaInvoiceIssuedEntity).IsTaxDocument,
                        LinkedDocument: item.LinkedDocument,
                        ExchangeRate: item.ExchangeRate,
                        OrigData: {
                            Amount: item.Amount,
                            ClearedAmount: item.ClearedAmount
                        },
                        Amount: item.Amount,
                        ClearedAmount: item.ClearedAmount,
                        TransactionAmount: item.TransactionAmount,
                        SplitAccountAssignments: item.SplitAccountAssignments,
                        Gains: gains
                    };
                    if (this.hasAccountAssignment) {
                        pairedDocuments[key] = {
                            ...pairedDocuments[key],
                            DateAccountingTransaction: item.DateAccountingTransaction,
                            AccountAssignmentSelection: item.AccountAssignmentSelection
                        };
                    }

                    if (isDefined(item[BindingContext.NEW_ENTITY_ID_PROP])) {
                        pairedDocuments[key] = {
                            ...pairedDocuments[key],
                            [BindingContext.NEW_ENTITY_ID_PROP]: item[BindingContext.NEW_ENTITY_ID_PROP]
                        };
                    }
                }

                if (item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain || item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeLoss) {
                    // in DB value is always positive (gain or loss) is indicated by type
                    // but we need to have it like the sign matters
                    // so now on we ignore type and calculate it from sign of the amount
                    const _convertedGain = item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeLoss ? Math.abs(item.Amount) * -1 : item.Amount;

                    pairedDocuments[key] = {
                        ...pairedDocuments[key],
                        ExchangeGain: _convertedGain
                    };
                }
            }
        }

        this.origPairedDocuments = { ...pairedDocuments };
        this.props.storage.setCustomData({ rootStorage: this.props.rootStorage });
        this.props.storage.setCustomData({ pairedDocuments: pairedDocuments });
        this.getRootStorage().setCustomData({
            pairedStorage: this.props.storage
        });
        this.props.storage.data.tableAction = PAIR_ACTION_ID;
    }

    getTransactionExchangeRate = (): number => {
        return this.props.rootStorage.data.entity.ExchangeRatePerUnit ?? 1;
    };

    getTransactionCurrency = (): string => {
        return this.props.rootStorage.data.entity?.TransactionCurrency?.Code ?? this.props.rootStorage.data.entity?.TransactionCurrencyCode;
    };

    formatCurrency = (val: number): string => {
        const currency = this.getTransactionCurrency();
        return formatCurrency(val, currency);
    };

    getPairedAmounts = (): { transactionAmount: number, amount: number } => {
        const docs = (this.props.rootStorage.data.entity.Items || []).filter((link: IEntity) => {
            return !link.LinkedDocument?.Id;
        }).concat(Object.values(this.getPairedDocuments()));

        let transactionAmount = 0;
        let amount = 0;

        for (const doc of docs) {
            transactionAmount += doc.TransactionAmount || 0;
            amount += doc.Amount || 0;
        }

        return {
            transactionAmount,
            amount
        };
    };

    getRestAmount = (): number => {
        const totalAmount = Math.abs(this.props.rootStorage.data.entity.TransactionAmount || 0);
        const paidAmount = this.getPairedAmounts().transactionAmount;

        return roundToDecimalPlaces(2, totalAmount - paidAmount);
    };

    getPairingTitleValue = (): string => {
        return this.formatCurrency(this.getRestAmount());
    };

    getStaticToolbarItems = (): TToolbarItem[] => {
        const balanceValue = this.getRestAmount();
        const item = <PairingTitle data-testid={TestIds.PairingValueTitle} key={"pairing-title"}>
            {`${this.props.rootStorage.t("Document:Pair.ToPairTitle")}:`}
            <PairingValue value={balanceValue}
                          data-testid={TestIds.PairingValue}>{this.getPairingTitleValue()}</PairingValue>
        </PairingTitle>;

        return [item];
    };

    renderAmountDialog = (): ReactElement => {
        return <AmountDialog
            needConfirmDialog={this.needConfirmDialog}
            rootStorage={this.props.rootStorage}
            onClose={() => {
                this.needConfirmDialog = null;
                this.forceUpdate();
                this.rerenderTable();
            }}
            storage={this.props.storage}/>;
    };
}

export { PairTableView as PairTableViewForExtend };


